import { type FC, useEffect, useState } from "react";
import { SidebarContent } from "@capacit-com/lara-law-shared-ui";
import { useSidebar } from "@capacit-com/lara-law-shared-ui";
import useUsers from "@/hooks/useUsers";
import useUserTokenStore from "@/stores/user-token.store";
import { Permission } from "@/lib/permissions/Permission.enum";
import AppRoutes from "@/constants/routes/AppRoutes";
import {
  navItems,
  adminNavItems,
  type NavigationItemType,
} from "@/constants/routes/NavItems";
import { User } from "@/models/users/User";
import useSidebarStore from "@/stores/sidebar.store";

import SidebarGroupSection from "./content-components/SidebarGroupSection";
import useFilteredNavItems from "./hooks/useFilteredNavItems";
import { SettingsIcon, HelpCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import SupportModal from "@/components/dialogs/SupportModal";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
} from "@capacit-com/lara-law-shared-ui";
import {} from "@capacit-com/lara-law-shared-ui";
import { PermissionGate } from "@/lib/permissions/PermissionGate";

const AppSidebarContent: FC = () => {
  const { open } = useSidebar();
  const { token } = useUserTokenStore();
  const hasPermission = useHasPermission();
  const { useQueryMe } = useUsers();
  const fetchMe = useQueryMe();

  const [user, setUser] = useState<User | null>(null);
  const { collapsedGroups, toggleGroup } = useSidebarStore();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const { filteredNavItems, filteredAdminNavItems } = useFilteredNavItems({
    user,
    token,
    hasPermission,
    adminNavItems,
    navItems,
    settingsDefaultPath: () => {
      if (
        hasPermission({
          oneOf: [
            Permission.CreateToneOfVoice,
            Permission.UpdateToneOfVoice,
            Permission.DeleteToneOfVoice,
          ],
        })
      )
        return AppRoutes?.settingsToneOfVoice ?? "/settings/tone-of-voice";
      if (hasPermission({ allOf: [Permission.ReadRoles] }))
        return "/settings/user-management";
      if (hasPermission({ allOf: [Permission.ReadFeatureFlag] }))
        return "/settings/features";
      if (hasPermission({ allOf: [Permission.ManageWidgetTemplates] }))
        return AppRoutes.settingsWidgetTemplates;
      return AppRoutes?.accessDenied ?? "/access-denied";
    },
  });

  useEffect(() => {
    if (fetchMe.isFetched && fetchMe.data) {
      setUser(fetchMe.data);
    }
  }, [fetchMe.isFetched, fetchMe.data]);

  const currentPath = window.location.pathname;

  // Group admin items by subOf
  const groupAdminItems = () => {
    const grouped: Record<string, NavigationItemType[]> = {};
    const ungrouped: NavigationItemType[] = [];

    filteredAdminNavItems.forEach((item) => {
      if (item.subOf) {
        grouped[item.subOf] = grouped[item.subOf] || [];
        grouped[item.subOf].push(item);
      } else ungrouped.push(item);
    });

    return { grouped, ungrouped };
  };

  const { grouped: groupedAdmin, ungrouped: ungroupedAdmin } =
    groupAdminItems();

  return (
    <SidebarContent className="flex flex-col justify-between overflow-x-hidden">
      <motion.div
        key="sidebar-sections"
        initial={false}
        animate={{
          opacity: open ? 1 : 0.8,
        }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="flex flex-col justify-between flex-1"
      >
        <div className="flex flex-col gap-2">
          <SidebarGroupSection
            title="APP"
            open={open}
            items={filteredNavItems}
            collapsed={open ? collapsedGroups.app : false}
            toggle={() => toggleGroup("app")}
            currentPath={currentPath}
          />
        </div>

        <div className="flex flex-col gap-1">
          {filteredAdminNavItems.length > 0 && (
            <SidebarGroupSection
              title="ADMIN"
              open={open}
              items={ungroupedAdmin}
              groupedItems={groupedAdmin}
              collapsed={open ? collapsedGroups.admin : false}
              toggle={() => toggleGroup("admin")}
              currentPath={currentPath}
              collapsibleSubGroups
              indentSubItems
              sliceNumber={3}
              iconResolver={(groupName) =>
                groupName === "Indstillinger" ? (
                  <SettingsIcon className="size-4" />
                ) : null
              }
            />
          )}

          {/* Support button */}
          <PermissionGate allOf={[Permission.CreateHubspotTicket]}>
            <SidebarGroup className={`px-2 py-0 ${!open ? "px-1.5" : ""}`}>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip="Support"
                      className="cursor-pointer"
                      onClick={() => setIsSupportModalOpen(true)}
                    >
                      <HelpCircle className="size-4" />
                      {open && <span>Support</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </PermissionGate>
        </div>
      </motion.div>
      <PermissionGate allOf={[Permission.CreateHubspotTicket]}>
        <SupportModal
          open={isSupportModalOpen}
          onOpenChange={setIsSupportModalOpen}
        />
      </PermissionGate>
    </SidebarContent>
  );
};

export default AppSidebarContent;
